import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  apiInvestigados,
  type CandidatoDeIdentidade,
  type Caso,
  type ConexaoEncontrada,
  type ConsultaExterna,
  type GrauDeParentesco,
  type Investigado,
  QUALIFICACOES,
  TIPOS_DE_DADO,
  type QualificacaoInvestigado,
  type TipoDoDado,
} from '../lib/api.ts';
import { maiuscula } from '../lib/caixa.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { usarEhMaster } from '../lib/papel.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { BlocoDeLancar, CampoDoPasso } from './PassoAPasso.tsx';

/**
 * A ficha base dos investigados do caso.
 *
 * > *"A partir das informações que temos e preenchermos nessa mini ficha base é
 * > o que as APIs e pesquisas farão."* — Richard, 16/08/2026
 *
 * É o ponto de partida de toda pesquisa: sem nome e documento não há de onde
 * consultar nada. Vive **dentro do caso** porque investigado é atributo do
 * caso, como nível e prazo — e porque um segundo relatório do mesmo caso não
 * pode obrigar a redigitar tudo.
 *
 * ⚠️ **É o dado mais sensível do Hub.** Descreve gente que não é cliente e não
 * consentiu. Não entra na busca global, não vai ao Marketing, não aparece em
 * log — ver `api/src/rotas/investigados.ts`.
 */

/** `00001333920258260334` → `0000133-39.2025.8.26.0334`. Guardado sem pontuação. */


/** A ajuda de cada uma, para quem cadastra não ter de adivinhar. */
const AJUDA_QUALIFICACAO: Record<QualificacaoInvestigado, string> = {
  devedor: 'Quem responde pela dívida.',
  avalista: 'Quem garantiu a dívida de outro.',
  socio_nao_avalista:
    'Pessoa que é sócia e NÃO assinou como garantidora — responde por outro caminho, não pelo aval.',
  grupo_economico: 'As empresas do lado empresarial, e as interpostas pessoas jurídicas.',
  grupo_familiar:
    'Parentes e vinculados. Cônjuge e filhos entram sempre — é onde patrimônio camuflado costuma aparecer.',
};

const GRAUS_ESCRITOS: Record<string, string> = {
  pai: 'Pai', mae: 'Mãe', filho: 'Filho(a)',
  avo: 'Avô(ó)', neto: 'Neto(a)', irmao: 'Irmão(ã)',
  bisavo: 'Bisavô(ó)', bisneto: 'Bisneto(a)', tio: 'Tio(a)', sobrinho: 'Sobrinho(a)',
  conjuge: 'Cônjuge',
};


/**
 * O painel em si, sem moldura.
 *
 * ⚠️ **Serve em dois lugares, e por isso não carrega diálogo dentro.** Abre-se
 * pelo cartão do caso e vive **dentro do relatório** — pedido do Richard,
 * 16/08/2026: *"em todos os relatórios precisam ter a parte dos investigados
 * para preencher, editar e apagar"*. Duas cópias do mesmo painel divergiriam na
 * primeira correção feita só numa delas.
 */
export function PainelDeInvestigados({
  casoId,
  limiteDoNivel,
  /** Quando é o primeiro acesso e não há ninguém, já abre o formulário. */
  abrirVazio = false,
  /**
   * Clicar no nome leva ao relatório do caso — pedido do Richard, 18/08/2026.
   *
   * ⚠️ **Opcional de propósito.** Dentro do próprio relatório o painel também
   * aparece, e ali o nome não pode levar a lugar nenhum: já se está lá.
   */
  aoAbrirRelatorio,
  /**
   * Avisa quem hospeda o painel de que a ficha mudou.
   *
   * ⚠️ **Existe porque o quadro de ativos e o painel de ativos vivem na mesma
   * tela do relatório, e leem o mesmo caso.** Sem este aviso, acrescentar um
   * investigado deixaria os dois com o número velho — e a casa já escreveu a
   * regra: desenho velho na tela é pior que desenho nenhum, porque quem olha
   * acredita nele.
   *
   * ⚠️ **Não dispara na montagem**, só em mudança de verdade: quem acabou de
   * carregar não precisa mandar todo mundo carregar de novo.
   */
  aoMudarAFicha,
}: {
  casoId: string;
  /**
   * O teto do nível — 22 no N3, 12 no N2, 5 no N1, nulo no Customizado sem
   * limite declarado.
   *
   * ⚠️ **Era `contratados`, o número digitado no cadastro do caso, e o campo
   * saiu em 20/08/2026.** Sem alguém para digitá-lo, o valor virava o default
   * `1` — e o aviso dispararia em todo caso. O teto do nível é cláusula de
   * contrato, e é com ele que a ficha se compara.
   */
  limiteDoNivel: number | null;
  abrirVazio?: boolean;
  aoAbrirRelatorio?: () => void;
  aoMudarAFicha?: () => void;
}) {
  const [lista, setLista] = useState<Investigado[] | null>(null);
  /* ⚠️ **Carrega a ORIGEM, não um sim/não** — são dois botões que abrem o mesmo
     formulário para caminhos diferentes de descoberta. `null` = fechado. */
  const [novo, setNovo] = useState<'cliente' | 'documental' | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const ehMaster = usarEhMaster();

  async function carregar() {
    try {
      const r = (await apiInvestigados.listar(casoId)).investigados;
      setLista(r);
      // Ficha vazia com o formulário fechado é uma tela que não diz o que
      // fazer. Aberta, ela pede o que precisa.
      if (abrirVazio && r.length === 0) setNovo('cliente');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui carregar os investigados.');
    }
  }

  /** Recarrega a ficha e avisa a tela ao redor. Ver `aoMudarAFicha`. */
  async function recarregar() {
    await carregar();
    aoMudarAFicha?.();
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId]);

  return (
    <>
      {erro && <p className="erro">{erro}</p>}

      {lista === null ? (
        <p className="vazio">Carregando…</p>
      ) : lista.length === 0 && !novo ? (
        <p className="vazio">
          Nenhum investigado cadastrado. Comece pelos que o cliente informou
          {limiteDoNivel !== null && ` — este nível inclui até ${limiteDoNivel}`}.
        </p>
      ) : (
        lista.map((i) => (
          <FichaDeUm
            key={i.id}
            investigado={i}
            ehMaster={ehMaster}
            aoMudar={recarregar}
            aoErrar={setErro}
            aoAbrirRelatorio={aoAbrirRelatorio}
          />
        ))
      )}

      {novo ? (
        <FormularioDeInvestigado
          casoId={casoId}
          origem={novo}
          aoCancelar={() => setNovo(null)}
          aoSalvar={async () => {
            setNovo(null);
            await recarregar();
          }}
        />
      ) : (
        <div className="acrescentar-investigado">
          <button type="button" className="botao-secundario" onClick={() => setNovo('cliente')}>
            Acrescentar investigado
          </button>
          {/*
            ⭐ **O segundo caminho — 08/09/2026, pedido dele.**

            > *"Incluir botão de acrescentar investigado que não foi localizado
            > na assertiva, mas através da análise de documentos, ex.: menor de
            > idade."*

            ⚠️ **São dois botões e não um seletor dentro do formulário**, porque
            a origem não é um campo que se preenche: é a resposta a *como você
            chegou nesta pessoa*, e ela já está decidida antes de abrir a ficha.
          */}
          <button type="button" className="botao-texto" onClick={() => setNovo('documental')}>
            Acrescentar quem apareceu num documento
          </button>
        </div>
      )}

      {/*
        O limite avisa, não bloqueia — a realidade do caso manda, e o aditivo se
        formaliza depois.

        ⚠️ **Compara com o TETO DO NÍVEL, e não com o número contratado —
        20/08/2026.** O campo "Investigados neste caso" saiu do cadastro, então
        o valor guardado virou o default `1`; avisar contra ele diria "a ficha
        tem 3 e foi contratado para 1" em todo caso do mundo. O teto do nível é
        cláusula de contrato e continua valendo.
      */}
      {lista && limiteDoNivel !== null && lista.length > limiteDoNivel && (
        <p className="ajuda-alerta ajuda-solta">
          A ficha tem {lista.length} investigados e este nível inclui até{' '}
          {limiteDoNivel}. Trate por aditivo ou por expansão de nível.
        </p>
      )}
    </>
  );
}

/** O mesmo painel, em diálogo — como o cartão do caso o abre. */
export function Investigados({
  caso,
  aoFechar,
  aoAbrirRelatorio,
  aoMudarAFicha,
}: {
  caso: Caso;
  aoFechar: () => void;
  aoAbrirRelatorio?: () => void;
  /**
   * ⚠️ **Avisa a ficha do cliente de que a contagem mudou — 20/08/2026.**
   *
   * Sem isto, cadastrar três investigados deixava o cartão do caso dizendo "1"
   * até alguém recarregar a página. O Richard viu, e tinha razão: número que só
   * fica certo depois do F5 é número em que não se confia.
   */
  aoMudarAFicha?: () => void;
}) {
  usarDialogo(aoFechar);

  return (
    <div
      className="cortina"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div
        className="dialogo dialogo-largo"
        role="dialog"
        aria-modal="true"
        aria-label="Investigados do caso"
      >
        <h2 className="titulo-secao">Investigados · {caso.codigo}</h2>
        <p className="ajuda ajuda-solta">
          O que se sabe de cada parte investigada. É daqui que as pesquisas partem —
          sem nome e documento não há de onde consultar.
        </p>

        <PainelDeInvestigados
          casoId={caso.id}
          limiteDoNivel={caso.limites.investigados}
          aoAbrirRelatorio={aoAbrirRelatorio}
          aoMudarAFicha={aoMudarAFicha}
        />

        <div className="dialogo-acoes">
          <button type="button" className="botao-primario" onClick={aoFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Um investigado, com seus telefones, e-mails, endereços e veículos. */
/**
 * O que as consultas trouxeram sobre este investigado.
 *
 * ⚠️ **Candidato e conexão ficam em blocos separados, e não é estética.**
 * Candidato é uma identidade POSSÍVEL do próprio investigado — adotar grava o
 * documento dele na ficha. Conexão é alguém LIGADO a ele. Se os dois
 * aparecessem na mesma lista com o mesmo botão, um clique errado gravaria o CPF
 * do irmão no lugar do devedor, e daí em diante o Hub investigaria a pessoa
 * errada num documento assinado.
 */
function PainelDeConsultas({
  investigado,
  aoMudar,
  aoErrar,
  ehMaster,
}: {
  investigado: Investigado;
  aoMudar: () => Promise<void>;
  aoErrar: (e: string) => void;
  ehMaster: boolean;
}) {
  const [dados, setDados] = useState<{
    consultas: ConsultaExterna[];
    candidatos: CandidatoDeIdentidade[];
    conexoes: ConexaoEncontrada[];
  } | null>(null);
  const [aberto, setAberto] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setDados(await apiInvestigados.consultas(investigado.id));
    } catch (e) {
      aoErrar(e instanceof Error ? e.message : 'Não consegui ler as consultas.');
    }
  }, [investigado.id, aoErrar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function buscarAgora() {
    setOcupado(true);
    try {
      await apiInvestigados.buscar(investigado.id);
      await carregar();
      await aoMudar();
    } catch (e) {
      aoErrar(e instanceof Error ? e.message : 'A busca não foi.');
    } finally {
      setOcupado(false);
    }
  }

  async function buscarVeiculos() {
    setOcupado(true);
    try {
      const r = await apiInvestigados.veiculos(investigado.id);
      await carregar();
      await aoMudar();
      // Zero é resposta, e precisa ser dita: pagou-se para saber que não há.
      avisar(
        r.encontrados === 0
          ? 'Nenhum veículo em nome deste documento.'
          : `${r.encontrados} veículo(s) · ${r.gravados} novo(s) na ficha.`,
      );
    } catch (e) {
      aoErrar(e instanceof Error ? e.message : 'A busca de veículos não foi.');
    } finally {
      setOcupado(false);
    }
  }

  async function promover(conexaoId: string) {
    setOcupado(true);
    try {
      const r = await apiInvestigados.promover(investigado.id, conexaoId);
      await carregar();
      await aoMudar();
      // ⚠️ O aviso de cota aparece e não impede — a realidade do caso manda.
      avisar(r.aviso ?? `Agora são ${r.naFicha} investigados no caso.`);
    } catch (e) {
      aoErrar(e instanceof Error ? e.message : 'Não consegui trazer para o caso.');
    } finally {
      setOcupado(false);
    }
  }

  async function adotar(candidatoId: string) {
    setOcupado(true);
    try {
      await apiInvestigados.adotar(investigado.id, candidatoId);
      await carregar();
      await aoMudar();
    } catch (e) {
      aoErrar(e instanceof Error ? e.message : 'Não consegui adotar este candidato.');
    } finally {
      setOcupado(false);
    }
  }

  const candidatos = dados?.candidatos ?? [];
  const conexoes = dados?.conexoes ?? [];
  const consultas = dados?.consultas ?? [];

  return (
    <div className="consultas-do-investigado">
      <div className="consultas-topo">
        <button
          type="button"
          className="botao-secundario"
          onClick={buscarAgora}
          disabled={ocupado}
        >
          {ocupado ? 'Buscando…' : 'Buscar agora'}
        </button>
        {/*
          ⚠️ **O preço fica NO BOTÃO, e não numa confirmação depois.** R$ 15,41 é
          treze vezes o conjunto cadastral, e é a consulta que faz o plano de
          R$ 700 acabar em 45 cliques. Quem clica precisa saber antes, não
          descobrir na fatura.

          ⚠️ **Só o master vê este botão** — e não é hierarquia: o colaborador
          não enxerga valor nenhum no Hub, então pedir que autorize um gasto cujo
          tamanho não pode ver seria pedir decisão às cegas. O servidor recusa
          também, com `SO_MASTER`: esconder o botão sem recusar a rota é
          decoração.
        */}
        {ehMaster && investigado.documento && (
          <button
            type="button"
            className="botao-texto"
            onClick={buscarVeiculos}
            disabled={ocupado}
            title="Consulta paga, feita só quando você pede"
          >
            Buscar veículos · R$ 15,41
          </button>
        )}
        {consultas.length > 0 && (
          <button type="button" className="botao-texto" onClick={() => setAberto(!aberto)}>
            {consultas.length} consulta{consultas.length > 1 ? 's' : ''} ·{' '}
            {aberto ? 'esconder' : 'ver'}
          </button>
        )}
      </div>

      {/*
        ⚠️ **Falha vem antes de tudo, e em vermelho.** O Hub vai rodar sozinho:
        consulta que não foi e ninguém viu deixa a ficha incompleta sem pista
        nenhuma. Aconteceu em 18/08/2026 — a Assertiva recusou por horário e o
        erro morreu num `catch` vazio.
      */}
      {consultas.filter((c) => c.falha).length > 0 && (
        <div className="bloco-falhas">
          {consultas
            .filter((c) => c.falha)
            .slice(0, 3)
            .map((c) => (
              <p key={c.id} className="erro erro-solto">
                <strong>{c.funcionalidade}</strong> não foi: {c.falha}
              </p>
            ))}
          <p className="ajuda">
            A ficha continua valendo — a busca é acréscimo. Corrija o motivo e use
            “Buscar agora”.
          </p>
        </div>
      )}

      {/*
        ⚠️ **Os candidatos vêm PRIMEIRO e em destaque**, porque enquanto houver
        candidato sem escolha a ficha está incompleta: sem documento, nenhuma
        outra consulta roda.
      */}
      {candidatos.length > 0 && !investigado.documento && (
        <div className="bloco-candidatos">
          <p className="ajuda-alerta ajuda-solta">
            A busca achou {candidatos.length} pessoa{candidatos.length > 1 ? 's' : ''} que
            pode{candidatos.length > 1 ? 'm' : ''} ser esta. <strong>Confira a filiação e a
            cidade antes de escolher</strong> — adotar o documento errado faz o Hub
            investigar outra pessoa.
          </p>
          {candidatos.map((c) => (
            <div key={c.id} className="candidato">
              <div>
                <strong>{c.nome}</strong>
                {c.documento && <span className="dado"> · {c.documento}</span>}
                {c.detalhe && <p className="ajuda">{c.detalhe}</p>}
                <p className="ajuda">{c.relacao}</p>
              </div>
              <button
                type="button"
                className="botao-secundario"
                onClick={() => adotar(c.id)}
                disabled={ocupado || !c.documento}
              >
                É esta
              </button>
            </div>
          ))}
        </div>
      )}

      {conexoes.length > 0 && (
        <div className="bloco-conexoes">
          <p className="rotulo">Vínculos encontrados</p>
          {conexoes.map((x) => (
            <div key={x.id} className="conexao">
              <div>
                <span className="etiqueta etiqueta-vinculo">{x.relacao}</span>{' '}
                <strong>{x.nome}</strong>
                {x.documento && <span className="dado"> · {x.documento}</span>}
                {x.detalhe && <span className="ajuda"> · {x.detalhe}</span>}
              </div>
              {/*
                ⚠️ **"Trazer para o caso", e não "marcar como laranja".** O
                botão descreve o ATO — passa a investigar esta pessoa —, nunca a
                conclusão. Rotular alguém de laranja é acusação, e num Hub que
                gera documento assinado a palavra não pode sair de um botão.

                ⚠️ **Não dispara consulta paga.** Promover é decidir que a
                pessoa interessa; buscar o patrimônio dela é a decisão seguinte,
                e custa R$ 15,41 em veículos.
              */}
              {x.virouInvestigadoId ? (
                <span className="ajuda">já é investigado</span>
              ) : (
                <button
                  type="button"
                  className="botao-texto"
                  onClick={() => promover(x.id)}
                  disabled={ocupado}
                  title="Passa a investigar esta pessoa, guardando o vínculo com o investigado de origem"
                >
                  Trazer para o caso
                </button>
              )}
            </div>
          ))}
          {/*
            ⚠️ Vínculo NÃO vira investigado por clique aqui — ainda. É o passo 4
            da fila, e depende da regra do "laranja": quem decide é a Karen, o
            Hub apresenta. Ver `docs/PRD.md` §19.12.4.
          */}
        </div>
      )}

      {aberto && (
        <div className="historico-consultas">
          {consultas.map((c) => (
            <div key={c.id} className="linha-consulta">
              <span>{c.funcionalidade}</span>
              <span className="ajuda">
                {c.itens} item{c.itens === 1 ? '' : 's'}
                {c.reconsulta && ' · reconsulta'}
                {c.custo && ` · R$ ${c.custo}`}
              </span>
              {/* ⚠️ A finalidade LGPD declarada é o que responde, um dia, sob
                  que base a KAIRÓS consultou esta pessoa. Fica à vista. */}
              {c.finalidade && <span className="ajuda">{c.finalidade}</span>}
              {c.falha && <span className="erro">não foi</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FichaDeUm({
  investigado,
  ehMaster,
  aoMudar,
  aoErrar,
  aoAbrirRelatorio,
}: {
  investigado: Investigado;
  ehMaster: boolean;
  aoMudar: () => Promise<void>;
  aoErrar: (e: string) => void;
  aoAbrirRelatorio?: () => void;
}) {
  const [tipo, setTipo] = useState<TipoDoDado>('telefone');
  const [valor, setValor] = useState('');
  const [nota, setNota] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [corrigindo, setCorrigindo] = useState(false);

  async function consultarSocietario() {
    if (consultando) return;
    setConsultando(true);
    try {
      const r = await apiInvestigados.societario(investigado.id);
      await aoMudar();
      avisar(
        r.encontradas === 0
          ? 'Nenhuma participação societária encontrada.'
          : `${r.encontradas} participação(ões) encontrada(s)` +
              (r.escreveuNoRelatorio ? ', e escritas na seção do relatório.' : '.') +
              (r.podeSerHomonimo ? ' Sem CPF na ficha: pode haver homônimo.' : ''),
      );
    } catch (e) {
      avisarErro(e);
    } finally {
      setConsultando(false);
    }
  }

  async function acrescentar(e: FormEvent) {
    e.preventDefault();
    if (!valor.trim()) return;
    try {
      await apiInvestigados.acrescentarDado(investigado.id, {
        tipo,
        valor,
        nota: nota.trim() || null,
      });
      setValor('');
      setNota('');
      await aoMudar();
    } catch (err) {
      avisarErro(err);
    }
  }

  const porTipo = (t: TipoDoDado) => investigado.dados.filter((d) => d.tipo === t);

  if (corrigindo) {
    return (
      <FormularioDeInvestigado
        casoId={investigado.casoId}
        inicial={investigado}
        aoCancelar={() => setCorrigindo(false)}
        aoSalvar={async () => {
          setCorrigindo(false);
          await aoMudar();
        }}
      />
    );
  }

  return (
    <article className="investigado">
      <div className="contrato-topo">
        <div className="contrato-identidade">
          {/*
            ⚠️ **O nome leva ao relatório do caso** — pedido do Richard,
            18/08/2026. É `<button>` e não `<a>` porque não há endereço próprio
            de investigado: o relatório é do CASO, e o investigado é atributo
            dele. Um link mentiria sobre para onde vai.

            ⚠️ **A FORMA do botão vem da classe, nunca do elemento** — a
            armadilha que já mordeu aqui: `.botao-*` no elemento errado herda
            só a cor e sai sem altura.
          */}
          {aoAbrirRelatorio ? (
            <button type="button" className="nome-que-abre" onClick={aoAbrirRelatorio}>
              {investigado.nome}
            </button>
          ) : (
            <strong>{investigado.nome}</strong>
          )}
          <span className={`etiqueta etiqueta-qual-${investigado.qualificacao}`}>
            {QUALIFICACOES[investigado.qualificacao]}
          </span>
          {investigado.documento && (
            <span className="dado">{investigado.documentoFormatado}</span>
          )}
        </div>
        {/* ⚠️ **"Pesquisar de novo" SAIU em 20/08/2026**, com os demais botões
            de consulta. A função `consultarSocietario` continua no arquivo. */}
        {/*
          ⚠️ **As ações vivem num bloco SÓ, e isso conserta um desalinhamento.**

          `.contrato-topo` é flex com `justify-content: space-between`. Com três
          filhos — identidade, "Editar", "Apagar" — o espaço que sobra é dividido
          ENTRE eles, e o "Editar" acaba onde o nome do investigado deixar: numa
          razão social longa ele ia para um lado, num nome curto para outro.
          O Richard viu isso em 20/08/2026.

          Com dois filhos, `space-between` faz o que se espera: identidade à
          esquerda, ações à direita, no mesmo lugar em todos os cartões.

          ⚠️ **É o mesmo defeito do cabeçalho do Hub**, que trocou flex por
          grade de três colunas pela mesma razão: `space-between` distribui a
          sobra, e a sobra depende do conteúdo.
        */}
        <div className="investigado-acoes">
          {/* Corrigir a ficha é o trabalho do dia — nome que veio errado do
              cliente, CPF que apareceu depois. Fica para todo mundo, como
              cadastrar. Apagar é que é do master. */}
          <button type="button" className="botao-texto" onClick={() => setCorrigindo(true)}>
            Editar
          </button>
          {ehMaster && (
            <button
              type="button"
              className="botao-texto botao-perigo"
              onClick={() => setConfirmando(true)}
            >
              Apagar
            </button>
          )}
        </div>
      </div>

      {/* ⭐ **A janelinha da casa — 19/09/2026.** Era um "Sim · Não" na
          própria linha, e é o apagar que mais leva junto. A lista da frase é o
          que o banco apaga em cascata com a parte; as consultas feitas e os
          campos de escrita do relatório ficam. */}
      {confirmando && (
        <Confirmar
          titulo="Apagar esta parte?"
          descricao={`${investigado.nome} sai do caso, e com a parte sai tudo o que foi lançado sobre ela: bens, atos, processos, passivo, vínculos e dados. Não volta.`}
          rotuloAcao="Apagar a parte"
          aoFechar={() => setConfirmando(false)}
          aoConfirmar={async () => {
            try {
              await apiInvestigados.apagar(investigado.id);
              await aoMudar();
            } catch (e) {
              aoErrar(e instanceof Error ? e.message : 'Não consegui apagar.');
            }
          }}
        />
      )}


      {/*
        ⚠️ **A FICHA FOI REDUZIDA AO CADASTRO — 20/08/2026.**

        > *"Dentro de 'investigados', no caso, gostaria que houvesse apenas os
        > investigados que o cliente nos passou. Ou seja, pode retirar todos os
        > botões, integrações e automações que houver na ficha dos
        > investigados."* — Richard

        **Saíram daqui:** a lista de dados apurados (telefones, e-mails,
        endereços, veículos, processos) · o formulário de acrescentar dado · o
        painel de consultas com custo e protocolo · os vínculos encontrados · o
        botão "Trazer para o caso" · "Buscar veículos" · "Pesquisar de novo".

        ⚠️ **Nada foi apagado do banco.** Os 44 dados apurados e as 19 consultas
        continuam guardados — só não aparecem. E **a Assertiva continua
        configurada**: credencial, cliente e funções intactos, prontos para
        religar onde o Richard apontar (`docs/CATALOGO-DE-AUTOMACOES.md`).

        ⚠️ **A ficha agora é o que o CLIENTE passou, e nada mais** — que é
        exatamente o que ele pediu.
      */}
    </article>
  );
}

/**
 * Cadastrar ou corrigir um investigado.
 *
 * ⚠️ **É o mesmo formulário nos dois casos, de propósito.** Um formulário para
 * criar e outro para editar divergem no primeiro campo novo que alguém
 * acrescenta só num deles — e aí o cadastro pede algo que a correção não deixa
 * mudar.
 */
/**
 * ⭐ **Exportado em 08/09/2026** para o tópico Investigados do relatório usar o
 * mesmo formulário — decisão dele: o botão fica nos dois lugares.
 *
 * ⚠️ **Um formulário só, e não uma cópia.** Dois cadastros para a mesma ficha
 * divergiriam na primeira correção feita só num deles — e aqui a divergência
 * seria em campo que decide classificação de patrimônio.
 */
export function FormularioDeInvestigado({
  casoId,
  inicial = null,
  origem = 'cliente',
  aoCancelar,
  aoSalvar,
}: {
  casoId: string;
  /** Quando vem, o formulário corrige em vez de cadastrar. */
  inicial?: Investigado | null;
  /**
   * ⭐ **De onde a pessoa veio.** `cliente` é o cadastro de sempre;
   * `documental` é quem a KAIRÓS achou lendo um documento — o menor de idade
   * que nenhuma consulta traz.
   *
   * ⚠️ **Só vale ao CRIAR.** Corrigir uma ficha não muda de onde ela veio: a
   * origem é registro de auditoria, não campo de edição.
   */
  origem?: 'cliente' | 'documental';
  aoCancelar: () => void;
  aoSalvar: () => Promise<void>;
}) {
  const [tipo, setTipo] = useState<'pf' | 'pj'>(inicial?.tipo ?? 'pf');
  const [nome, setNome] = useState(inicial?.nome ?? '');
  const [documento, setDocumento] = useState(inicial?.documentoFormatado ?? '');
  const [qualificacao, setQualificacao] = useState<QualificacaoInvestigado>(
    inicial?.qualificacao ?? 'devedor',
  );
  const [nascimentoEm, setNascimento] = useState(inicial?.nascimentoEm ?? '');
  const [nomeMae, setMae] = useState(inicial?.nomeMae ?? '');
  const [nomePai, setPai] = useState(inicial?.nomePai ?? '');
  const [grau, setGrau] = useState<GrauDeParentesco | null>(inicial?.grauParentesco ?? null);
  const [parenteDe, setParenteDe] = useState<string | null>(inicial?.parenteDeId ?? null);
  const [salvando, setSalvando] = useState(false);

  /*
   * Os outros investigados do caso, para o "de quem". ⚠️ Sem o próprio: ninguém
   * é parente de si mesmo, e o servidor recusa — melhor não oferecer o erro.
   */
  const [outros, setOutros] = useState<Array<{ id: string; nome: string }>>([]);
  useEffect(() => {
    void apiInvestigados
      .listar(casoId)
      .then((r) => setOutros(r.investigados.filter((o) => o.id !== inicial?.id)))
      .catch(() => setOutros([]));
  }, [casoId, inicial?.id]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (salvando || !nome.trim()) return;
    setSalvando(true);
    const dados = {
      tipo,
      nome,
      documento: documento || null,
      qualificacao,
      nascimentoEm: nascimentoEm || null,
      nomeMae: nomeMae || null,
      nomePai: nomePai || null,
      /*
       * ⚠️ **Fora do grupo familiar, os dois vão nulos.** O servidor recusa grau
       * em quem não é família, e mandar o que sobrou de uma troca de classe faria
       * a gravação falhar com uma mensagem que ninguém entenderia.
       *
       * ⚠️ **Grau escrito à mão nasce CONFIRMADO.** Quem digitou apurou; a
       * marcação de "a confirmar" é só para o que o Hub deduziu sozinho.
       */
      grauParentesco: qualificacao === 'grupo_familiar' ? grau : null,
      parenteDeId: qualificacao === 'grupo_familiar' ? parenteDe : null,
      ...(qualificacao === 'grupo_familiar' && grau ? { parentescoAConfirmar: false } : {}),
    };
    try {
      if (inicial) await apiInvestigados.editar(inicial.id, dados);
      /* ⚠️ A origem só vai na CRIAÇÃO — corrigir a ficha não muda de onde ela
         veio, e o servidor recusa `promovido` vindo da tela de qualquer modo. */
      else await apiInvestigados.criar(casoId, { ...dados, origemNoCaso: origem });
      await aoSalvar();
    } catch (err) {
      avisarErro(err);
      setSalvando(false);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (o bloco de lançar), no mesmo lugar de
     antes. ⚠️ A ficha continua reduzida aos quatro campos de 20/08/2026 — as
     colunas de nascimento, filiação e parentesco seguem no banco, fora da tela. */
  return (
    <BlocoDeLancar
      titulo={inicial ? `Editar ${inicial.nome}` : origem === 'documental' ? 'Acrescentar quem apareceu num documento' : 'Acrescentar investigado'}
      aoEnviar={(e) => void enviar(e)}
      acoes={
        <>
          <button type="button" className="imv-bt fantasma" onClick={aoCancelar}>
            Cancelar
          </button>
          {/* ⚠️ "Salvando e pesquisando…" saiu junto com a pesquisa: rótulo que
              anuncia o que não acontece é pior que rótulo genérico. */}
          <button type="submit" className="imv-bt primario" disabled={salvando || !nome.trim()}>
            <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : inicial ? 'Salvar' : 'Cadastrar investigado'}
          </button>
        </>
      }
    >
      <div className="imv-campo c2">
        <span className="imv-rotulo">Pessoa</span>
        <div className="imv-seg" role="radiogroup" aria-label="Pessoa física ou jurídica">
          {(['pf', 'pj'] as const).map((t) => (
            <button key={t} type="button" role="radio" aria-checked={tipo === t} className={`imv-op neutro${tipo === t ? ' on' : ''}`} onClick={() => setTipo(t)}>
              <i />
              {t === 'pf' ? 'Física' : 'Jurídica'}
            </button>
          ))}
        </div>
      </div>
      <CampoDoPasso rotulo="Qualificação" col={2} nota={AJUDA_QUALIFICACAO[qualificacao]}>
        <select id="inv-qual" value={qualificacao} onChange={(e) => setQualificacao(e.target.value as QualificacaoInvestigado)}>
          {(Object.keys(QUALIFICACOES) as QualificacaoInvestigado[]).map((q) => (
            <option key={q} value={q}>
              {QUALIFICACOES[q]}
            </option>
          ))}
        </select>
      </CampoDoPasso>
      <CampoDoPasso rotulo={tipo === 'pf' ? 'CPF' : 'CNPJ'} col={2} mono>
        <input id="inv-doc" inputMode="numeric" value={documento} onChange={(e) => setDocumento(e.target.value)} />
      </CampoDoPasso>
      <CampoDoPasso rotulo={tipo === 'pf' ? 'Nome completo' : 'Razão social'} obrigatorio col={6}>
        <input id="inv-nome" value={nome} onChange={(e) => setNome(maiuscula(e.target.value))} autoFocus />
      </CampoDoPasso>
    </BlocoDeLancar>
  );
}
